import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { BookOpenCheck, CreditCard, Database, Download, LayoutDashboard, LayoutTemplate, LogOut, Tags, Upload, Users, Wand2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandLockup, PoweredByAdmissionHall } from "@/components/Brand";

const LINKS = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/upload", label: "Upload data", icon: Upload },
  { to: "/admin/batches", label: "Batches & audit", icon: Database },
  { to: "/admin/masters", label: "Masters library", icon: BookOpenCheck },
  { to: "/admin/export", label: "Export data", icon: Download },
  { to: "/admin/mappings", label: "Category mapping", icon: Wand2 },
  { to: "/admin/aliases", label: "Alias mapping", icon: Tags },
  { to: "/admin/overview", label: "Landing content", icon: LayoutTemplate },
  { to: "/admin/users", label: "Users & access", icon: Users, ownerOnly: true },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, ownerOnly: true },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const links = LINKS.filter((l) => !l.ownerOnly || user?.is_super_admin);

  return (
    <div className="flex min-h-screen bg-slate-100">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-slate-200 bg-white lg:flex">
        <div className="border-b border-slate-200 px-5 py-4">
          <BrandLockup size="md" wordmark="always" />
          <p className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">Admin console</p>
          <PoweredByAdmissionHall className="mt-1 block" />
        </div>
        <nav className="flex-1 p-2">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`admin-nav-${l.label.split(" ")[0].toLowerCase()}`}
              className={({ isActive }) =>
                `mb-0.5 flex items-center gap-2 px-3 py-2 text-sm font-medium ${
                  isActive ? "bg-[#1A365D] text-white" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-4 text-xs text-slate-500">
          <p data-testid="admin-user-label">{user?.email}</p>
          <Link to="/app/allotment" data-testid="goto-user-app" className="mt-2 block hover:text-[#1A365D]">
            Open user app →
          </Link>
          <button
            data-testid="admin-logout-btn"
            onClick={async () => {
              await logout();
              nav("/admin/login");
            }}
            className="mt-2 flex items-center gap-1 hover:text-rose-600"
          >
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto p-4 lg:p-6">
        <div className="mx-auto max-w-[1400px] space-y-5">
          <div className="flex gap-1 overflow-x-auto lg:hidden">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`admin-mobilenav-${l.label.split(" ")[0].toLowerCase()}`}
                className={({ isActive }) =>
                  `whitespace-nowrap border px-3 py-1.5 text-xs font-semibold ${
                    isActive ? "border-[#1A365D] bg-[#1A365D] text-white" : "border-slate-300 bg-white text-slate-600"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button
              data-testid="admin-mobile-logout-btn"
              aria-label="Log out"
              title="Log out"
              onClick={async () => {
                await logout();
                nav("/admin/login");
              }}
              className={
                "shrink-0 border border-slate-300 bg-white px-3 py-1.5 " +
                "text-slate-600 hover:text-rose-600"
              }
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
